import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ClubHero from "../../components/VirtualWorld/ClubHero";
import MatchReportCard from "../../components/VirtualWorld/MatchReportCard";
import ModeTile from "../../components/VirtualWorld/ModeTile";
import SponsorMark from "../../components/VirtualWorld/SponsorMark";
import StatPill from "../../components/VirtualWorld/StatPill";
import Loading from "../../components/Loading";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";

const cr = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

const ManagerHome = () => {
  const { userId, manager, loading, error, refresh } = useVirtualWorld();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(() => location.state?.flash || "");
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="space-y-3 max-w-md">
        <p className="text-red-600">{error}</p>
        <button
          type="button"
          onClick={() => refresh()}
          className="px-4 py-2 rounded-lg bg-zinc-800 text-white text-sm font-semibold"
        >
          Erneut laden
        </button>
      </div>
    );
  }

  if (!manager?.club) {
    const offer = manager?.foundingOffer || {};
    const startBudget = Number(offer.startBudget) || 50000;
    const create = async () => {
      setBusy(true);
      setMsg("");
      try {
        await vw.createClub(userId, name);
        await refresh();
      } catch (err) {
        const detail =
          err?.data?.message ||
          (typeof err?.data === "string" ? err.data : null) ||
          err?.message ||
          (err?.status ? `HTTP ${err.status}` : null);
        setMsg(detail || "Club konnte nicht gegründet werden.");
      } finally {
        setBusy(false);
      }
    };
    return (
      <div className="space-y-6 max-w-xl mx-auto">
        <div>
          <p className="text-xs uppercase tracking-widest opacity-50 mb-2">Neuer Manager</p>
          <h2 className="text-3xl font-black mb-2">Dein Club braucht einen Namen.</h2>
          <p className="opacity-70 mb-4">
            Mehr brauchst du jetzt nicht. Danach lernst du die Mannschaft kennen — Schritt für Schritt.
          </p>
          <p className="text-sm rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 mb-4">
            Startkapital: <strong>{cr(startBudget)}</strong>
            {offer.reduced
              ? ` (Neustart nach ${
                  offer.lastReason === "insolvency" ? "Insolvenz" : "Abgabe"
                }${offer.lastClubName ? ` von ${offer.lastClubName}` : ""})`
              : " (voller Einstieg)"}
          </p>
        </div>
        <input
          className="w-full p-3 rounded-xl border mb-4 text-black"
          placeholder="z. B. Black Arrows oder Gasthof Doppel"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {msg ? <p className="text-red-600 mb-3">{msg}</p> : null}
        <button
          disabled={busy || name.trim().length < 3}
          onClick={create}
          className="px-6 py-3 bg-amber-700 text-amber-50 rounded-xl font-semibold"
        >
          Club übernehmen
        </button>
      </div>
    );
  }

  const club = manager.club;
  const story = manager.story || {};
  const unlocks = manager.progression?.manager?.unlocks || {};
  const guide = manager.guide || manager.progression?.manager?.guide;
  const step = guide?.current;
  const guided = Boolean(manager.guided ?? story.guided ?? guide?.guided);
  const nextTask = story.nextTask || manager.progression?.manager?.next;
  const squad = manager.players?.filter((p) => !p.loanedOut) || [];
  const phase = Number(manager.onboardingPhase || manager.progression?.manager?.onboardingPhase || 1);

  const skip = async () => {
    if (!window.confirm("Gesamte Begleitung überspringen und alle Bereiche freischalten?")) return;
    setBusy(true);
    setMsg("");
    try {
      await vw.skipOnboarding(userId);
      await refresh();
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Überspringen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const worldBuild = manager.worldBuild || {};
  const challenge = manager.challenge || null;

  const inviteFriends = async () => {
    const path = worldBuild.invitePath || "/fightclub/manager";
    const url = `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
    const text =
      worldBuild.inviteHint ||
      `Du willst mehr Mannschaften kennenlernen? Spiel mit mir Darts Manager im FightClub: ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "FightClub Manager", text, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setMsg("Einladungslink kopiert.");
      } else {
        setMsg(url);
      }
    } catch (_) {
      setMsg(url);
    }
  };

  const onChallengePrimary = async () => {
    setBusy(true);
    setMsg("");
    try {
      if (challenge?.kind === "discover" && challenge.opponent?.id) {
        await vw.discoverOpponent(userId, challenge.opponent.id);
        await refresh();
      } else {
        navigate("/manager/match");
      }
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const onChallengeSecondary = async () => {
    setBusy(true);
    setMsg("");
    try {
      await vw.declineRematch(userId);
      await refresh();
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Konnte nicht verschoben werden.");
    } finally {
      setBusy(false);
    }
  };

  // Begleitung: eine Sache, ein Button — kein Hero, keine Stats, kein Timer-Alarm
  if (guided && step) {
    return (
      <div className="max-w-lg mx-auto space-y-10 py-6">
        <div>
          <p className="text-xs uppercase tracking-widest opacity-40 mb-3">
            Schritt {step.index} von {step.total}
          </p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">{step.title}</h2>
          <p className="text-base sm:text-lg opacity-70 leading-relaxed mb-8">{step.hint}</p>
          <Link
            to={step.to || "/manager"}
            className="inline-flex px-7 py-3.5 rounded-xl bg-amber-700 text-amber-50 font-bold text-lg"
          >
            {step.cta || "Weiter"}
          </Link>
        </div>
        <div className="pt-6 border-t border-gray-200/80 dark:border-gray-700/80">
          {msg ? <p className="text-red-600 mb-2 text-sm">{msg}</p> : null}
          <button
            type="button"
            disabled={busy}
            onClick={skip}
            className="text-xs underline opacity-35 hover:opacity-70"
          >
            Alles freischalten (ohne Begleitung)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ClubHero
        title={story.title || `Mein Club: ${club.displayName || club.name}`}
        subtitle={
          story.subtitle ||
          "Du hast gerade die Leitung eines kleinen Dartclubs übernommen."
        }
        imageUrl={club.headerImage || story.headerImage}
        badge={club.statusLabel || "Amateurclub"}
        kids={
          <>
            {club.sponsor?.brand ? (
              <div className="mb-3 inline-flex items-center gap-2 rounded-xl bg-black/25 border border-amber-100/25 px-3 py-2">
                <SponsorMark brand={club.sponsor.brand} size={28} />
                <span className="text-sm font-semibold">
                  {club.sponsor.name}
                  {club.sponsor.placementLabel ? ` · ${club.sponsor.placementLabel}` : ""}
                </span>
              </div>
            ) : null}
            {nextTask && nextTask.title !== "Alles erklärt" && nextTask.key !== "home" ? (
              <div className="rounded-2xl bg-black/25 border border-amber-100/25 p-4 backdrop-blur-sm">
                <div className="text-xs uppercase tracking-wide text-amber-100/80 mb-1">Nächste Aufgabe</div>
                <div className="font-bold text-xl mb-1">{nextTask.title}</div>
                <p className="text-sm opacity-90 mb-3">{nextTask.hint}</p>
                <Link
                  to={nextTask.to || "/manager/team"}
                  className="inline-flex px-5 py-2.5 rounded-xl bg-amber-200 text-amber-950 font-bold"
                >
                  {nextTask.cta || "Weiter"}
                </Link>
              </div>
            ) : null}
          </>
        }
      />

      <div className="grid sm:grid-cols-3 gap-3">
        <StatPill emoji="🧑‍🤝‍🧑" label="Kader" value={`${story.squadSize || squad.length} Spieler`} />
        <StatPill emoji="🎯" label="Nächstes Match" value={story.nextMatchLabel || "Freundschaftsspiel"} />
        <StatPill emoji="💰" label="Budget" value={cr(club.budget)} />
      </div>

      {Number(club.bankDebt) > 0 ? (
        <div className="rounded-2xl border border-sky-400/70 bg-sky-50 dark:bg-sky-950/40 px-4 py-3 text-sm">
          <p className="font-bold mb-0.5">Bank / Notkredit: {cr(club.bankDebt)} offen</p>
          <p className="opacity-75">
            Wird an positiven Spieltagen teilweise getilgt. Jugendspieler ohne Ablöse halten den Kader am Laufen.
          </p>
        </div>
      ) : null}

      {(club.starterKeys || []).length < 4 ? (
        <div className="rounded-2xl border border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-4">
          <p className="font-bold mb-1">
            Starting Four {(club.starterKeys || []).length}/4 — Wettbewerbe brauchen 4 Starter
          </p>
          <p className="text-sm opacity-80 mb-3">
            Spieler im Kader allein reichen nicht. Stelle vier Starter auf, sonst erscheint
            „Wettbewerbe“ nicht (oder Spiele lassen sich nicht anpfeifen).
          </p>
          <Link
            to="/manager/team"
            className="inline-flex px-4 py-2 rounded-xl bg-amber-700 text-amber-50 font-semibold"
          >
            Zur Mannschaft
          </Link>
        </div>
      ) : null}

      {challenge ? (
        <div className="rounded-3xl border border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-900 p-6 shadow">
          <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">
            {challenge.kind === "rematch" ? "Revanche" : challenge.kind === "discover" ? "Neuer Verein" : "Neue Herausforderung"}
          </div>
          <h2 className="text-2xl font-black mb-2">{challenge.title}</h2>
          <p className="opacity-90 mb-4">{challenge.body}</p>
          {challenge.opponent ? (
            <div className="flex gap-4 items-center mb-5">
              <img
                src={
                  challenge.opponent.headerImage ||
                  `https://api.dicebear.com/7.x/shapes/svg?seed=${encodeURIComponent(challenge.opponent.name)}`
                }
                alt=""
                className="w-20 h-20 rounded-2xl object-cover border border-amber-200"
              />
              <div>
                <div className="font-bold text-xl">{challenge.opponent.name}</div>
                <div className="text-sm opacity-70">{challenge.opponent.isAi ? "PC-Verein" : "Echter Managerclub"}</div>
                {challenge.opponent.motto ? <p className="text-sm mt-1 opacity-80">{challenge.opponent.motto}</p> : null}
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onChallengePrimary}
              className="px-5 py-2.5 rounded-xl bg-amber-700 text-amber-50 font-semibold"
            >
              {challenge.cta}
            </button>
            {challenge.secondaryCta ? (
              <button
                type="button"
                disabled={busy}
                onClick={onChallengeSecondary}
                className="px-5 py-2.5 rounded-xl bg-white dark:bg-gray-700 border font-semibold"
              >
                {challenge.secondaryCta}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {worldBuild?.needed ? (
        <div className="rounded-3xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-5">
          <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Mehr Vereine entdecken</div>
          <div className="text-2xl font-black mb-1">
            {worldBuild.ready}/{worldBuild.needed} Vereine in der Region
          </div>
          <p className="text-sm opacity-80 mb-4">
            {worldBuild.leagueStarted
              ? "Die Liga läuft — du kannst trotzdem Freundschaftsspiele spielen."
              : worldBuild.inviteHint ||
                "Lade Freunde ein. Bis die Liga startet, lernst du Gegner in Freundschaftsspielen kennen."}
          </p>
          {!worldBuild.leagueStarted ? (
            <button
              type="button"
              onClick={inviteFriends}
              className="px-5 py-2.5 rounded-xl bg-emerald-700 text-white font-semibold"
            >
              Freunde einladen
            </button>
          ) : null}
        </div>
      ) : null}

      {phase === 2 && manager.lastReport ? <MatchReportCard report={manager.lastReport} /> : null}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {unlocks.team ? <ModeTile to="/manager/team" emoji="🧑‍🤝‍🧑" title="Team" hint="Kader und Verträge" /> : null}
        {unlocks.match ? <ModeTile to="/manager/match" emoji="🏆" title="Wettbewerbe" hint="Liga, FlowLiga, Turniere" featured /> : null}
        {unlocks.training ? <ModeTile to="/manager/training" emoji="🏋️" title="Training" hint="Nach dem Match gezielt arbeiten" /> : null}
        {unlocks.league ? <ModeTile to="/manager/league" emoji="📊" title="Liga-Zentrale" hint="Tabelle und Spielplan" /> : null}
        {unlocks.transfers ? <ModeTile to="/manager/transfers" emoji="🔁" title="Transfers" hint="Echte Avatare verpflichten" /> : null}
        {unlocks.inbox ? <ModeTile to="/manager/inbox" emoji="📬" title="Inbox" hint="Boardroom" /> : null}
        {unlocks.club ? <ModeTile to="/manager/club" emoji="🏢" title="Geschäftsstelle" hint="Sponsor und Büro" /> : null}
        {unlocks.venue ? (
          <ModeTile
            to="/manager/venue"
            emoji="🏟️"
            title="Heimstätte"
            hint={manager.venue?.tier?.label || "Ausbauen bis zur Arena"}
          />
        ) : null}
        {unlocks.hall ? <ModeTile to="/manager/hall" emoji="🏛️" title="Ruhm" hint="Erfolge" /> : null}
      </div>

      {msg ? <p className="text-sm opacity-80">{msg}</p> : null}
    </div>
  );
};

export default ManagerHome;
